import * as THREE from 'three';

/**
 * How much of a mage to draw, by how far away it is.
 *
 * A mage is not one draw call. Each is eleven to fourteen meshes with its own
 * cloned materials, so twenty-four of them is a little over three hundred
 * skinned primitives — and with shadows on, every one is drawn twice. Six
 * hundred draw calls at the fifty microseconds of CPU each costs in Three.js is
 * thirty milliseconds a frame, against a budget of sixteen. That is the stutter,
 * and it is why it scaled with the number of bots and vanished as they died.
 *
 * None of it is GPU work. A 3080 is not troubled by three hundred skinned
 * meshes; the main thread is, walking that many objects and uploading that many
 * bone matrices every frame.
 *
 * The fog does most of the arguing for us. At the density the Cathedral is
 * played at, a mage sixty metres away is ninety per cent fogged and eighty
 * metres away is invisible — so beyond a certain distance the cost buys nothing
 * that can be seen.
 */

/**
 * Beyond this a mage stops casting a shadow, which halves its cost at a stroke.
 *
 * Deliberately close. In a room this dark a shadow past about fifteen metres is
 * a smudge under somebody's feet, and it is costing exactly as much as the mage
 * standing on it.
 */
export const SHADOW_DISTANCE = 16;
/**
 * Beyond this the skeleton stops being posed.
 *
 * The mesh still draws and still moves — it is the *animation* that stops, and a
 * figure whose legs are still at fifty metres in fog is not something anybody
 * has ever noticed.
 */
export const ANIMATE_DISTANCE = 34;
/** Beyond this, nothing is drawn at all. 91% fogged, and behind that, nothing. */
export const DRAW_DISTANCE = 55;

/**
 * How often a distant mage's skeleton is updated, in frames.
 *
 * Between the animation and the draw distances a mage is posed at a third of the
 * rate rather than not at all, so it does not visibly freeze as somebody walks
 * toward it.
 */
export const FAR_ANIMATION_EVERY = 3;

export interface Lod {
  draw: boolean;
  shadow: boolean;
  /** Whether the skeleton should be posed on this frame. */
  animate: boolean;
  /**
   * What to multiply the frame's `dt` by when posing this mage.
   *
   * A skeleton updated every third frame has to be handed three frames' worth of
   * time or it plays at a third speed — so the compensation belongs *with* the
   * decision to skip, which is here.
   *
   * It was previously worked out at the call site as `shadow ? 1 : 3`, and the
   * two conditions are not the same one. Shadows stop at 16m and animation
   * starts skipping at 34m, so every mage between those distances was posed
   * every frame and handed three frames of time: an eighteen-metre band in which
   * everybody ran at triple speed. That band is most of a fight in the
   * Cathedral, and it is the sort of fault that reads as "the animations look
   * wrong" rather than as a timestep bug.
   */
  dtScale: number;
}

/**
 * Works out what to do with one mage, given how far it is from the eye.
 *
 * `frame` staggers the far band so twenty distant mages do not all update their
 * skeletons on the same frame — which would replace a steady cost with a spike
 * every third frame, and a spike is what a player notices.
 */
export function lodFor(distance: number, frame: number, index: number): Lod {
  if (distance > DRAW_DISTANCE) {
    return { draw: false, shadow: false, animate: false, dtScale: 1 };
  }
  if (distance > ANIMATE_DISTANCE) {
    return {
      draw: true,
      shadow: false,
      animate: (frame + index) % FAR_ANIMATION_EVERY === 0,
      dtScale: FAR_ANIMATION_EVERY,
    };
  }
  return {
    draw: true, shadow: distance <= SHADOW_DISTANCE, animate: true, dtScale: 1,
  };
}

/**
 * Sets shadow casting across a mage, and only when it has actually changed.
 *
 * Walking the tree costs more than the flag it sets, and a mage's distance band
 * changes a few times a match rather than a few times a second.
 */
export function setShadowCasting(root: THREE.Object3D, on: boolean): void {
  root.traverse((node) => {
    if ((node as THREE.Mesh).isMesh) node.castShadow = on;
  });
}
